import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { infaqService } from '../../../services/infaqService';
import LoadingSpinner from '../../../components/LoadingSpinner';

function InfaqAnalyticsPage() {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await infaqService.getDetailedAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat analitik');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Gagal memuat analitik'}</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-[#0D7330] text-white rounded-lg hover:bg-[#0a5c26] transition-colors"
          >
            Cuba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analitik Infaq</h1>
          <p className="text-gray-600">Paparan statistik terperinci kempen dermaan</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Jumlah Dikutip</p>
                <p className="text-2xl font-bold text-[#0D7330]">
                  {infaqService.formatCurrency(analytics.overview.totalRaised)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Digunakan</p>
                <p className="text-2xl font-bold text-[#16592E]">
                  {infaqService.formatCurrency(analytics.overview.totalUtilized)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Baki</p>
                <p className="text-2xl font-bold text-gray-900">
                  {infaqService.formatCurrency(analytics.overview.totalRemaining)}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Jumlah Derma</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.overview.totalDonations}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Campaigns */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Kempen Teratas</h2>
            <div className="space-y-4">
              {analytics.topCampaigns.map((campaign: any, index: number) => (
                <div key={campaign.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-[#0D7330] text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <Link
                        to={`/infaq/campaign/${campaign.slug}`}
                        target="_blank"
                        className="font-semibold text-gray-900 hover:text-[#0D7330]"
                      >
                        {campaign.title}
                      </Link>
                      <p className="text-sm text-gray-600">{campaign.donorCount} penderma</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#0D7330]">
                      {infaqService.formatCurrency(campaign.currentAmount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Math.round((campaign.currentAmount / campaign.targetAmount) * 100)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Donation Stats by Status */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Statistik Derma mengikut Status</h2>
            <div className="space-y-4">
              {analytics.donationStatsByStatus.map((stat: any) => (
                <div key={stat.status} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        stat.status === 'PAID'
                          ? 'bg-green-500'
                          : stat.status === 'PENDING'
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                    />
                    <span className="font-medium text-gray-900">
                      {stat.status === 'PAID' ? 'Dibayar' : stat.status === 'PENDING' ? 'Menunggu' : 'Gagal'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{stat.count} derma</p>
                    <p className="text-sm text-gray-500">
                      {infaqService.formatCurrency(stat.totalAmount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Donations */}
          <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Dermaan Terkini</h2>
              <Link to="/admin/infaq/donations" className="text-[#0D7330] hover:underline font-medium">
                Lihat Semua
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Penderma</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Kempen</th>
                    <th className="text-right py-3 text-sm font-semibold text-gray-700">Jumlah</th>
                    <th className="text-right py-3 text-sm font-semibold text-gray-700">Tarikh</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recentDonations.map((donation: any) => (
                    <tr key={donation.id} className="border-b border-gray-100">
                      <td className="py-3 text-sm text-gray-900">
                        {donation.isAnonymous ? 'Tanpa Nama' : donation.donorName}
                      </td>
                      <td className="py-3 text-sm text-gray-600">{donation.campaign.title}</td>
                      <td className="py-3 text-sm text-right font-semibold text-[#0D7330]">
                        {infaqService.formatCurrency(donation.amount)}
                      </td>
                      <td className="py-3 text-sm text-right text-gray-600">
                        {new Date(donation.createdAt).toLocaleDateString('ms-MY')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InfaqAnalyticsPage;
